import { JupiterTheme, MockHook } from '@jupiterone/jupiter-web-toolkit';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import useAppsMenuLogic, { AppsMenuComponentProps } from './useAppsMenuLogic';
import { UserX } from 'react-feather';
import {
  Popper,
  Paper,
  LinearProgress,
  Tooltip,
  Fade,
  ClickAwayListener,
} from '@material-ui/core';
import AppsMenuItem from './AppsMenuItem';
import AppsIcon from './AppsIcon';
import { baseGridCell } from './AppsMenu.styles';

const useStyles = makeStyles((theme: JupiterTheme) => {
  return createStyles({
    appsIconContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    popWrap: {
      backgroundImage: theme.palette.gradient.j1Light,
      borderRadius: 0,
      marginTop: `${theme.spacing(2)}px`,
      paddingTop: 3,
      position: 'relative',
      maxWidth: 452,
      zIndex: 0,
      '&:after': {
        backgroundImage: theme.palette.gradient.j1,
        opacity: '0.5',
        content: `''`,
        display: 'block',
        mixBlendMode: 'multiply',
        height: 3,
        top: 0,
        position: 'absolute',
        width: '100%',
        zIndex: 1,
      },
    },
    popGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      overflow: 'hidden',
    },
    gridCellNoHover: {
      ...baseGridCell(theme).class,
    },
    bgIconGroup: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end',
      height: 1200,
      position: 'absolute',
      width: 1200,
      bottom: 50,
    },
    bgIconNoApps: {
      color: theme.palette.error.main,
      width: 48,
      height: 48,
      strokeWidth: 1.5,
    },
    noAppsContentGroup: {
      display: 'flex',
      justifyContent: 'center',
      lineHeight: 1.2,
      padding: '0 12px 5.5px',
      color: theme.palette.navy[400],
      flex: '0 0 auto',
      fontWeight: 500,
      letterSpacing: -0.25,
      textAlign: 'center',
      position: 'relative',
      bottom: -30,
    },
    noAppsContent: {
      borderRadius: 16,
      color: theme.palette.red[600],
      padding: '10px 16px 5px',
      whiteSpace: 'normal',
    },
    popperRoot: {
      zIndex: 1400,
    },
  });
});

export const AppsMenuComponent = ({
  anchorEl,
  setAnchorEl,
  clearAnchorEl,
  apps,
  lockedApps,
  loading,
}: AppsMenuComponentProps) => {
  const classes = useStyles();
  return (
    <>
      <Tooltip title="Apps" enterDelay={750} onClick={setAnchorEl}>
        <div
          data-testid="navbar-apps-icon"
          className={classes.appsIconContainer}
        >
          <>
            <AppsIcon aria-label="Apps" className={anchorEl ? 'active' : ''} />
          </>
        </div>
      </Tooltip>
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="top"
        transition
        className={classes.popperRoot}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <ClickAwayListener onClickAway={clearAnchorEl}>
              <Paper className={classes.popWrap}>
                {loading && <LinearProgress />}

                <nav className={classes.popGrid}>
                  {apps.length >= 1 ? (
                    apps.map((app) => (
                      <AppsMenuItem
                        key={app.name}
                        app={app}
                        locked={lockedApps[app.name]}
                        onClick={clearAnchorEl}
                      />
                    ))
                  ) : (
                    <div className={classes.gridCellNoHover}>
                      <div className={classes.bgIconGroup}>
                        <UserX className={classes.bgIconNoApps} />
                      </div>
                      <div className={classes.noAppsContentGroup}>
                        <p className={classes.noAppsContent}>
                          You do not have access to any apps, please contact
                          your account administrator if you think this is an
                          error.
                        </p>
                      </div>
                    </div>
                  )}
                </nav>
              </Paper>
            </ClickAwayListener>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default MockHook.withHookHoc(AppsMenuComponent, useAppsMenuLogic);
